import { api } from './api';

export type ActivityType = 'time' | 'expense';
export type ActivityInvoiceStatus = 'unbilled' | 'draft' | 'billed' | 'non_billable';

export interface Activity {
  id: string;
  chambersId: string;
  caseId: string | null;
  firmUserId: string;
  entryType: ActivityType;
  category: string | null;
  description: string;
  activityDate: string;
  durationMinutes: number | null;
  quantity: string | null;
  rate: string | null;
  amount: string;
  currency: string;
  isBillable: boolean;
  invoiceStatus: ActivityInvoiceStatus;
  feeNoteId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  case: { id: string; caseNumber: string; caseName: string } | null;
  firmUser: {
    id: string;
    firstName: string;
    lastName: string;
    memberType: string;
  };
}

export interface ListActivitiesQuery {
  type?: ActivityType;
  dateFrom?: string;
  dateTo?: string;
  invoiceStatus?: ActivityInvoiceStatus;
  category?: string;
  firmUserId?: string;
  caseId?: string;
  billable?: 'true' | 'false';
  page?: number;
  limit?: number;
}

export interface ListActivitiesResponse {
  activities: Activity[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ActivityTotals {
  totalMinutes: number;
  totalHours: number;
  billableAmount: number;
  billableCount: number;
  nonBillableAmount: number;
  nonBillableCount: number;
  expenseAmount: number;
  expenseCount: number;
  currency: string;
}

export async function fetchActivities(query: ListActivitiesQuery = {}): Promise<ListActivitiesResponse> {
  const res = await api.get<ListActivitiesResponse>('/activities', { params: query });
  return res.data;
}

export async function fetchActivityTotals(query: Omit<ListActivitiesQuery, 'page' | 'limit'> = {}): Promise<ActivityTotals> {
  const res = await api.get<{ totals: ActivityTotals }>('/activities/totals', { params: query });
  return res.data.totals;
}

/**
 * Categories this practice already uses, most-used first.
 *
 * Feeds the category picker so a barrister reuses a category rather than coining a
 * near-duplicate of it ("travel" alongside "Travel"), and the list screen's category filter.
 * Omit `type` for both vocabularies at once — the filter does that when Type is "All"; the
 * picker always names one, since a time category and an expense category are different lists.
 */
export async function fetchActivityCategories(type?: 'time' | 'expense'): Promise<string[]> {
  const res = await api.get<{ data: string[] }>('/activities/categories', {
    params: type ? { type } : undefined,
  });
  return res.data.data;
}

export type CreateTimePayload = {
  entryType: 'time';
  caseId?: string | null;
  category?: string | null;
  description: string;
  activityDate: string;
  durationMinutes: number;
  rate?: number;
  isBillable?: boolean;
  notes?: string | null;
};

export type CreateExpensePayload = {
  entryType: 'expense';
  caseId?: string | null;
  category?: string | null;
  description: string;
  activityDate: string;
  quantity?: number;
  amount: number;
  rate?: number;
  isBillable?: boolean;
  notes?: string | null;
};

export type CreateActivityPayload = CreateTimePayload | CreateExpensePayload;

export async function createActivity(payload: CreateActivityPayload): Promise<Activity> {
  const res = await api.post<{ activity: Activity }>('/activities', payload);
  return res.data.activity;
}

export interface UpdateActivityPayload {
  caseId?: string | null;
  category?: string | null;
  description?: string;
  activityDate?: string;
  durationMinutes?: number;
  quantity?: number;
  rate?: number;
  amount?: number;
  isBillable?: boolean;
  notes?: string | null;
}

export async function updateActivity(id: string, payload: UpdateActivityPayload): Promise<Activity> {
  const res = await api.patch<{ activity: Activity }>(`/activities/${id}`, payload);
  return res.data.activity;
}

export async function deleteActivity(id: string): Promise<void> {
  await api.delete(`/activities/${id}`);
}

export interface ConvertToFeeRecordPayload {
  amount?: number;
  description?: string;
  feeType?: string;
}

export async function convertToFeeRecord(
  activityId: string,
  overrides?: ConvertToFeeRecordPayload,
): Promise<{ id: string }> {
  const res = await api.post<{ feeRecord: { id: string } }>(
    `/activities/${activityId}/convert-to-fee-record`,
    overrides ?? {},
  );
  return res.data.feeRecord;
}
